import { objectKeys } from '@iconik-io/core/src/object_entries';
import {
  isPermission,
  type Permission,
} from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';

import type {
  CategoryName,
  CategoryObjectType,
} from '../ducks/pages/admin_metadata_categories/types';
import type { IconType } from '../icons/constants';
import { ICONS } from '../icons/icon';
import type { SelectOption } from '../ui/select_field/types';
import { DEFAULT_CATEGORY_NAME } from './metadata';
import type { VariantType } from './variants';

export const formats = [
  { label: 'ORIGINAL', value: 'ORIGINAL' },
  { label: 'LOWRES', value: 'LOWRES' },
  { label: 'OTHER', value: 'OTHER' },
];

export const booleansToYesNo = [
  { label: 'No', value: 'false' },
  { label: 'Yes', value: 'true' },
];

export const assetTypes = [
  { value: 'ASSET', label: 'Asset', icon: ICONS.File },
  {
    value: 'SEQUENCE',
    label: 'Sequence',
    icon: ICONS.Sequence,
  },
  {
    value: 'NLE_PROJECT',
    label: 'NLE project',
    icon: ICONS.Project,
  },
  {
    value: 'PLACEHOLDER',
    label: 'Placeholder',
    icon: ICONS.Placeholder,
  },
  {
    value: 'SUBCLIP',
    label: 'Subclip',
    icon: ICONS.Subclip,
  },
  {
    value: 'LINK',
    label: 'Link',
    icon: ICONS.Link,
  },
  { value: 'CUSTOM', label: 'Custom', icon: ICONS.Custom },
];

export type HistoryOperationData = {
  bsStyle: VariantType;
  label: string;
  value: string;
};
export const historyOperations: HistoryOperationData[] = [
  { value: 'EXPORT', label: 'Export', bsStyle: 'success' },
  { value: 'TRANSCODE', label: 'Transcode', bsStyle: 'success' },
  { value: 'ANALYZE', label: 'Analyze', bsStyle: 'success' },
  { value: 'ARCHIVE', label: 'Archive', bsStyle: 'info' },
  { value: 'RESTORE', label: 'Restore', bsStyle: 'info' },
  { value: 'DELETE_FORMAT', label: 'Delete Format', bsStyle: 'danger' },
  { value: 'DELETE_FILESET', label: 'Delete Fileset', bsStyle: 'danger' },
  { value: 'MODIFY_FILESET', label: 'Modify Fileset', bsStyle: 'info' },
  { value: 'RESTORE_FILESET', label: 'Restore Fileset', bsStyle: 'info' },
  { value: 'APPROVE', label: 'Approve', bsStyle: 'success' },
  { value: 'REJECT', label: 'Reject', bsStyle: 'danger' },
  { value: 'DOWNLOAD', label: 'Download', bsStyle: 'success' },
  { value: 'METADATA', label: 'Metadata', bsStyle: 'success' },
  { value: 'VERSION_CREATE', label: 'Create version', bsStyle: 'info' },
  { value: 'VERSION_DELETE', label: 'Delete version', bsStyle: 'danger' },
  { value: 'VERSION_UPDATE', label: 'Update version', bsStyle: 'success' },
  { value: 'VERSION_PROMOTE', label: 'Promote version', bsStyle: 'info' },
  { value: 'TRANSCRIPTION', label: 'Transcribe', bsStyle: 'success' },
];

export const permissionLabels: Record<Permission, string> = {
  read: 'Read',
  write: 'Write',
  delete: 'Delete',
  'change-acl': 'Edit Access',
};

export const permissions: Permission[] = objectKeys(permissionLabels);

// ACL permissions come off the API as plain strings, and the backend can add
// one before the frontend has a label for it.
export const permissionLabel = (permission: string): string =>
  isPermission(permission) ? permissionLabels[permission] : permission;

export const permissionsOptions: SelectOption[] = permissions.map(
  (permission) => ({
    label: permissionLabels[permission],
    value: permission,
  }),
);

export const customActionsContexts = [
  { label: 'Asset', value: 'ASSET' },
  { label: 'Collection', value: 'COLLECTION' },
  { label: 'Bulk', value: 'BULK' },
  { label: 'Saved Search', value: 'SAVED_SEARCH' },
  { label: 'Shared Asset', value: 'SHARED_ASSET' },
  { label: 'Shared Collection', value: 'SHARED_COLLECTION' },
  { label: 'Shared Playlist', value: 'SHARED_PLAYLIST' },
  { label: 'Subclip', value: 'ASSET_SUBCLIP' },
  { label: 'None', value: 'NONE' },
];

export const customActionsTypes = [
  { label: 'Post', value: 'POST' },
  { label: 'Open', value: 'OPEN' },
  { label: 'Publish', value: 'PUBLISH' },
];

export type ColorOptionType = { value: string; label: string };

// asset panels
export const ACLS_KEY = 'acls';
export const ASSET_METADATA_KEY = 'asset_metadata';
export const COMMENTS_KEY = 'comments';
export const FACE_RECOGNITION = 'face_recognition';
export const FILES_KEY = 'transcoders';
export const HISTORY_KEY = 'history';
export const POSTERS_KEY = 'posters';
export const PUBLISH_KEY = 'publish';
export const REVIEWS_KEY = 'reviewandapprove';
export const SHARES_KEY = 'shares';
export const SUBCLIPS_KEY = 'subclips';
export const TIME_BASED_METADATA_KEY = 'time_based_metadata';
export const TRANSCRIPTIONS_KEY = 'transcriptions';

export const RELATIONS_KEY = 'relations';

export const ASSET_PANELS_KEYS = [
  ACLS_KEY,
  ASSET_METADATA_KEY,
  COMMENTS_KEY,
  FACE_RECOGNITION,
  FILES_KEY,
  HISTORY_KEY,
  POSTERS_KEY,
  PUBLISH_KEY,
  RELATIONS_KEY,
  REVIEWS_KEY,
  SHARES_KEY,
  SUBCLIPS_KEY,
  TIME_BASED_METADATA_KEY,
  TRANSCRIPTIONS_KEY,
];

export const ASSET_PANELS_OPTIONS = [
  { label: 'Asset Metadata', value: ASSET_METADATA_KEY },
  { label: 'Asset Relations', value: RELATIONS_KEY },
  { label: 'Comments', value: COMMENTS_KEY },
  { label: 'Transcriptions', value: TRANSCRIPTIONS_KEY },
  { label: 'Review & Approve', value: REVIEWS_KEY },
  { label: 'Time-Based Metadata', value: TIME_BASED_METADATA_KEY },
  { label: 'Subclips', value: SUBCLIPS_KEY },
  { label: 'Files', value: FILES_KEY },
  { label: 'Posters', value: POSTERS_KEY },
  { label: 'Access Control', value: ACLS_KEY },
  { label: 'Shares', value: SHARES_KEY },
  { label: 'Publish', value: PUBLISH_KEY },
  { label: 'History', value: HISTORY_KEY },
];

// search panels

export const COLLECTION_ACLS_PANEL = 'collection_acls_panel';
export const COLLECTION_SHARES_PANEL = 'collection_shares_panel';

export const SEARCH_ASSET_METADATA_PANEL = 'search_asset_metadata_panel';
export const SEARCH_COLLECTIONS_PANEL = 'search_collections_panel';
export const SEARCH_FILTERS_PANEL = 'search_filters_panel';
export const SEARCH_HISTORY_PANEL = 'search_history_panel';
export const SEARCH_SAVED_SEARCHES_PANEL = 'search_saved_searches_panel';
export const SEARCH_SAVED_SEARCH_ACLS_PANEL = 'search_saved_search_acls_panel';
export const CUTTING_ROOM_PROJECT_PANEL = 'cutting_room_project_panel';

export const UPLOADS_PANEL = 'uploads_panel';

export const SEARCH_PANELS_KEYS = [
  COLLECTION_ACLS_PANEL,
  COLLECTION_SHARES_PANEL,
  CUTTING_ROOM_PROJECT_PANEL,
  SEARCH_ASSET_METADATA_PANEL,
  SEARCH_COLLECTIONS_PANEL,
  SEARCH_FILTERS_PANEL,
  SEARCH_HISTORY_PANEL,
  SEARCH_SAVED_SEARCHES_PANEL,
  SEARCH_SAVED_SEARCH_ACLS_PANEL,
];

export const SEARCH_PANELS_OPTIONS = [
  { label: 'Filters', value: SEARCH_FILTERS_PANEL },
  { label: 'Collections', value: SEARCH_COLLECTIONS_PANEL },
  { label: 'Saved searches', value: SEARCH_SAVED_SEARCHES_PANEL },
];

export const HOMEPAGE_COLLECTION = '/collection';
export const HOMEPAGE_SAVEDSEARCH = '/savedsearch';
export const HOMEPAGE_LAST_VISITED_PAGE = '__iconik_last_visited_page_';
export const HOMEPAGE_DASHBOARD = '/dashboard';

export const HOMEPAGE_OPTIONS: SelectOption[] = [
  { label: 'Search', value: '/search' },
  { label: 'Admin', value: '/admin' },
  { label: 'Jobs', value: '/admin/jobs' },
  { label: 'Upload', value: '/upload' },
  { label: 'Favorites', value: '/favorites' },
  { label: 'Saved Search', value: HOMEPAGE_SAVEDSEARCH },
  { label: 'Collection', value: HOMEPAGE_COLLECTION },
  { label: 'Last Visited Page', value: HOMEPAGE_LAST_VISITED_PAGE },
  { label: 'Dashboard', value: HOMEPAGE_DASHBOARD },
  {
    label: 'My Shares',
    value: '/my_shares',
  },
];

// Default sort values
export const ORDER_DATE_CREATED = 'date_created';
export const ORDER_TIME_START_MILLISECONDS = 'time_start_milliseconds';
export const ORDER_ASC = 'asc';
export const ORDER_DESC = 'desc';

export const metadataCategories: { [key in CategoryObjectType]: CategoryName } =
  {
    assets: DEFAULT_CATEGORY_NAME,
    collections: DEFAULT_CATEGORY_NAME,
    segments: 'generic',
    search: DEFAULT_CATEGORY_NAME,
    custom_actions: DEFAULT_CATEGORY_NAME,
  };

export const metadataCategoryLabels: { [key in CategoryObjectType]: string } = {
  assets: 'Assets',
  collections: 'Collections',
  segments: 'Segments',
  search: 'Search',
  custom_actions: 'Custom Actions',
};

export const metadataCategoryIcons: { [key in CategoryObjectType]: IconType } =
  {
    assets: ICONS.Video,
    collections: ICONS.Collection,
    segments: ICONS.Comment,
    search: ICONS.Search,
    custom_actions: ICONS.CustomAction,
  };

// Local storage keys
export const LS_SEARCH_DEFAULT_PANELS = 'searchDefaultPanels';
export const LS_ASSETS_DEFAULT_PANELS = 'assetDefaultPanels';

// Field name prefix for the bulk update mode
export const FIELD_UPDATE_MODE_PREFIX = '__iconik_field_update_mode__';

// Field name prefix for the marker field, which is a checkbox and used to mark
// metadata fields to update in bulk mode
export const FIELD_UPDATE_BULK_PREFIX = '__iconik_field_update_bulk__';

// Browser unsaved changes prompt message
export const UNSAVED_CHANGES_MESSAGE =
  'You have unsaved data. Are you sure you want to proceed?';

// All Applications id value for allowed IPs - System Settings
export const DEFAULT_APP_ID_ALLOWED_IPS = 'all_apps';

export const FAKE_SUBCLIP_SEGMENT_ID = 'fakeSubclipSegmentId';

export const DEFAULT_FACE_CONFIRMATION_MODAL_VISIBILITY = true;
